import React from "react";
import { motion } from "framer-motion";


export default function About() {
  return (
    <div className="bg-[#f3f0e4] text-[#105132]">
      {/* Brand Vision */}
      <section
        className="py-20 px-6 text-center max-w-5xl mx-auto relative"
        style={{
          backgroundImage: "url('/texture/soft-paper.png')",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <motion.h2
          className="text-4xl font-bold mb-6 relative z-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Our Vision
        </motion.h2>
        <motion.p
          className="text-lg leading-relaxed text-[#105132]/90 relative z-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          The Nutrafox is a new-age superfood brand redefining how the world
          experiences Makhana. Rooted in purity and powered by nature, we
          source the finest foxnuts from the heartlands of India and deliver
          them to global shelves—raw, roasted, or deliciously flavored. Our
          mission is simple: to make snacking wholesome, guilt-free, and
          globally accessible. With a fierce commitment to quality and
          sustainability, The Nutrafox isn’t just a snack brand—it’s a lifestyle
          choice for the mindful, modern consumer.
        </motion.p>
      </section>

      {/* Founders' Message */}
      <section className="bg-[#fdf8ee] py-20 px-6">
        <motion.h3
          className="text-3xl font-bold text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          Founders' Message
        </motion.h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {[
            {
              name: "Abhishek Kumar",
              role: "Co-Founder",
              message:
                "For me, Nutrafox is personal. It's about turning our native food wisdom into something global. We're building not just a brand, but trust.",
              img: "/founders/founder1.jpeg",
            },
            {
              name: "Priyanka Mishra",
              role: "Co-Founder",
              message:
                "My goal was always to bring India’s best to the world. With Nutrafox, we're exporting purity wrapped in purpose.",
              img: "/founders/founder2.png",
            },
          ].map((person, i) => (
            <motion.div
              key={i}
              className="bg-white p-8 rounded-2xl shadow-lg border border-[#c0a230] text-center flex flex-col items-center"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: i * 0.2 }}
            >
              <img
                src={person.img}
                alt={person.name}
                className="w-24 h-24 rounded-full object-cover border-4 border-[#c0a230] mb-4"
              />
              <h4 className="text-xl font-bold mb-1">{person.name}</h4>
              <p className="text-sm font-medium text-[#236e47] mb-2">
                {person.role}
              </p>
              <p className="text-sm text-[#105132]/90">{person.message}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Sourcing & Quality Commitment */}
      <section className="py-20 px-6 max-w-6xl mx-auto">
        <motion.h3
          className="text-3xl font-bold text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Our Sourcing & Quality Commitment
        </motion.h3>
        <div className="bg-[#fdf8ee] rounded-3xl p-8 md:p-12 shadow-md flex flex-col md:flex-row gap-10 items-center">
          <motion.img
            src="/elements/sourcing.png"
            alt="Sourcing"
            className="w-full max-w-[320px] object-cover rounded-2xl shadow-md"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          />
          <motion.div
            className="text-center md:text-left"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-lg leading-relaxed text-[#105132]/90 mb-4">
              From the water-rich lands of Bihar to certified export-grade
              packaging, we ensure each batch of Makhana meets international
              standards. Our farms and facilities are monitored for
              sustainability, hygiene, and freshness at every stage.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-4">
              {["Handpicked", "Sun-dried", "No Preservatives", "Export-ready"].map(
                (label, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#c0a230] bg-gradient-to-br from-[#e5ecd9] to-[#fdf8ee] text-[#105132] font-semibold text-sm shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.8"
                      stroke="#105132"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {label}
                  </span>
                )
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="bg-[#f6f3ea] py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <motion.h3
            className="text-3xl sm:text-4xl font-semibold text-center text-[#105132]"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            Certifications
          </motion.h3>

          <motion.p
            className="mt-3 text-center text-sm sm:text-base text-[#105132]/70 max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Trusted quality standards that support export-grade sourcing, hygiene, and compliance.
          </motion.p>

          {/* Trust strip card */}
          <motion.div
            className="mt-10 rounded-3xl bg-white/70 backdrop-blur
                      ring-1 ring-black/5 shadow-sm
                      px-6 py-10 sm:px-10"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 items-center">
              {[
                { name: "FSSAI", img: "/icons/fssai.png" },
                { name: "APEDA", img: "/icons/apeda.jpg" },
                { name: "ISO", img: "/icons/ISO.png" },
                { name: "HACCP", img: "/icons/HACCP.png" },
              ].map((cert, i) => (
                <motion.div
                  key={cert.name}
                  className="group flex items-center justify-center"
                  initial={{ opacity: 0, scale: 0.96 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  viewport={{ once: true }}
                >
                  <div
                    className="relative w-full max-w-[220px] sm:max-w-[240px]
                              rounded-2xl bg-white
                              ring-1 ring-[#c0a230]/25
                              shadow-sm
                              px-5 py-6
                              transition
                              hover:-translate-y-1 hover:shadow-md hover:ring-[#c0a230]/50"
                    title={cert.name}
                  >
                    {/* subtle glow */}
                    <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition">
                      <div className="absolute -top-6 -right-6 h-16 w-16 rounded-full bg-[#D4AF57]/20 blur-xl" />
                      <div className="absolute -bottom-6 -left-6 h-16 w-16 rounded-full bg-[#3E8E41]/15 blur-xl" />
                    </div>

                    {/* logo */}
                    <img
                      src={cert.img}
                      alt={cert.name}
                      className="relative mx-auto
                                  h-20 sm:h-24
                                  w-auto
                                  object-contain
                                  transition-transform duration-300
                                  group-hover:scale-105"
                      loading="lazy"
                      draggable={false}
                    />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Small divider line + note */}
            <div className="mt-8 flex items-center justify-center">
              <div className="h-px w-24 bg-black/10" />
            </div>
            <p className="mt-4 text-center text-xs sm:text-sm text-black/50">
              Logos shown for trust & compliance representation.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
